import React from 'react';
import { Table, Tbody, Td, Tr } from '@chakra-ui/react';
import { Box } from 'components';
import { observer } from 'mobx-react';

interface Indexable {
  [state: string]: any; // indexer
}

export function getProp(node: Indexable, accessor: string) {
  // TODO: use either 'object-path' or 'resultFromPathSegments'
  if (!accessor) {
    // sometimes we need the whole object for a column
    return node;
  }
  const segs: string[] = accessor.split('.');
  let val = node;
  try {
    segs.forEach(seg => {
      val = val[seg];
    });
    return val as any;
  } catch (e) {
    console.warn(e);
    return null;
  }
}

const tableCellStyle = {
  borderColor: 'gray.200',
  height: '60px',
  color: 'black',
  padding: 0,
};

const tableCellContentStyle = {
  height: '60px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '5px 24px',
};

const renderColumnSubRows = (rows: any[], SubCell: any) => {
  return rows.map((row: any, index) => {
    let tableCell = <Td key={index} {...tableCellStyle} />;

    if (SubCell) {
      tableCell = (
        <Td key={index} {...tableCellStyle}>
          <Box sx={{ paddingLeft: '24px' }}>{SubCell(row)}</Box>
        </Td>
      );
    }

    return (
      <Tr
        id={row.id}
        key={row.id}
        height='60px'
        position='relative'
        borderTopWidth={1}
      >
        {tableCell}
      </Tr>
    );
  });
};

type ActionButtonWrapperProp = {
  children: React.ReactNode;
  isRowExpanded: boolean;
};

const ActionButtonsWrapper = ({
  children,
  isRowExpanded,
}: ActionButtonWrapperProp) => {
  return (
    <td
      style={{
        position: 'sticky',
        right: 24,
      }}
    >
      <div
        style={{
          position: 'absolute',
          right: 0,
          ...(isRowExpanded ? { top: 13 } : { transform: 'translateY(-50%)' }),
        }}
      >
        {children}
      </div>
    </td>
  );
};

interface TableRowProps {
  loading: boolean;
  index: number;
  subRows?: any[];
  columns: any;
  nodes: any;
  node: any;
  conn?: any;
  tableStore?: any;
  RowActionButtons?: any; // ReactComponent
  handleMouseOver: (id: number) => void;
  handleMouseLeave: () => void;
  isHovered?: boolean;
}

type CellProps = {
  data: any;
  column: any;
  conn: any;
  rowState: any;
};

export const TableRow = observer(
  ({
    loading,
    index,
    subRows = [],
    columns,
    nodes,
    node,
    conn,
    tableStore,
    RowActionButtons,
    handleMouseOver,
    handleMouseLeave,
    isHovered = false,
  }: TableRowProps) => {
    const isRowExpanded = tableStore?.expandableRowsState[node.id];

    const onToggleButtonClick = () => {
      tableStore.toggleRowExpanded(node.id);
    };

    const rowId = index;

    return (
      <Tr
        height='60px'
        id={`${rowId}`}
        position='relative'
        backgroundColor={
          isHovered && RowActionButtons ? 'gray.50' : 'transparent'
        }
        borderBottomWidth={nodes.length - 1 === index ? 0 : 1}
        onMouseOver={() => handleMouseOver(rowId)}
        onMouseLeave={handleMouseLeave}
      >
        {!loading &&
          columns.map((column: any, ix: number) => {
            const cellData = getProp(node, column.accessor);
            let element: React.ReactElement = null;
            const rowState = {
              isExpanded: isRowExpanded,
              onToggleExpand: onToggleButtonClick,
            };
            // if the column cell is a functional react component, render it
            // we add this column.component as it's close to impossible to detect a functional component reliably
            if (column.component) {
              const CellAlias =
                column.Cell as React.FunctionComponent<CellProps>;
              element = (
                <CellAlias
                  data={cellData}
                  column={column}
                  conn={conn}
                  rowState={rowState}
                />
              );
            } else {
              // else call the function and render the result
              element =
                column.Cell?.(cellData, column, conn, rowState) ?? cellData;
            }

            // TODO: remove padding from the cell when it has nested rows
            return (
              <Td
                key={ix}
                {...tableCellStyle}
                borderBottom='none'
                // textAlign={ix === columns.length - 1 ? 'right' : 'left'}
                isNumeric={column.isNumeric}
              >
                <Box
                  data-test-id={column.dataContentTestId}
                  sx={
                    isRowExpanded
                      ? tableCellContentStyle
                      : { padding: '5px 24px' }
                  }
                >
                  {element}
                </Box>
                {isRowExpanded && subRows?.length > 0 ? (
                  <Table>
                    <Tbody>
                      {renderColumnSubRows(subRows, column.SubCell)}
                    </Tbody>
                  </Table>
                ) : null}
              </Td>
            );
          })}
        {RowActionButtons && isHovered ? (
          <ActionButtonsWrapper isRowExpanded={isRowExpanded}>
            <RowActionButtons node={node} />
          </ActionButtonsWrapper>
        ) : null}
      </Tr>
    );
  },
);

export const TableRowMemo = React.memo(TableRow);
